import { dimensionSchema, type QuoteDimension } from "@workspace/api-zod/quantity-dimensions";
import { isConstructionRun, keyMeasurementGroup, parseMeasurementSubjects, type MeasurementSubject } from "@workspace/api-zod/measurement-review";
import type { MeasurementReviewField } from "./measurement-review";
import { recoverSubjectLinks, subjectRecognitionLabel } from "./measurement-subject-links";

export const subjectProfile: Record<MeasurementSubject["kind"], string> = {
  opening: "Openings", chimney: "Chimney", extension: "Extension", loft: "Loft", roof: "Roof",
  room: "Room finishes", wall: "Walls", groundworks: "Groundworks", external: "External works",
  steel: "Steel", floor: "Floor construction",
};
const axes: Record<MeasurementSubject["kind"], string[]> = {
  opening: ["width", "height"], chimney: ["width", "projection", "height"],
  extension: ["length", "width"], loft: ["length", "width"], roof: ["length", "width"],
  room: ["length", "width", "height"], wall: ["length", "height"],
  groundworks: ["length", "width", "depth"], external: ["length", "width"],
  steel: [], floor: [],
};
const isLimit = (e: QuoteDimension) => /\b(max(?:imum)?|min(?:imum)?|limit(?:ed)?)\b/i.test(`${e.label} ${e.note}`);

/** Footprint depth/projection is the second plan axis, not excavation depth.
 * Keep the original evidence label and provenance; only map it to a review slot.
 */
export function subjectMeasurementAxis(kind: MeasurementSubject["kind"] | undefined, label: string) {
  const axis = label.match(/\b(width|height|length|projection|depth|frontage|breadth)\b(?!.*\b(width|height|length|projection|depth|frontage|breadth)\b)/i)?.[0]?.toLowerCase();
  if (kind === "chimney" && axis === "depth") return "projection";
  if (kind === "extension" && !/\b(trench|foundation|footing|excavation|bearing)\b/i.test(label)) {
    if (axis === "depth" || axis === "projection") return "length";
    if (axis === "frontage" || axis === "breadth") return "width";
  }
  return axis;
}

/** Older descriptions can still expose explicitly named work areas without another AI call.
 * A replacement span is labelled as replacement work, never as a new demolition opening.
 * Numeric dimensions come only from the separately validated evidence, not from this text scan.
 */
export function inferMeasurementSubjects(description: string, evidence: QuoteDimension[]): MeasurementSubject[] {
  const subjects: MeasurementSubject[] = [];
  const seenLines = new Set<string>();
  const positive = description.replace(/\b(?:no|without|excluding)\b[^\n.;]*/gi, "");
  for (const [index, raw] of positive.split(/\r?\n/).entries()) {
    const line = raw.replace(/^\s*(?:[-*•]\s*|\d+(?:\.\d+)*[.)]?\s*)/, "").trim().split(/[.!?]\s+/)[0];
    const opening = /\b(?:form|create|widen|enlarge|alter)\b.{0,80}\b(?:opening|doorway)\b/i.test(line);
    const replacement = /^(?:remove|replace)\b.{0,35}\bexisting beam\b/i.test(line) &&
      /\breplac(?:e|ement)\b/i.test(line) && /\bsection\b/i.test(line);
    if (!opening && !replacement) continue;
    const key = line.toLowerCase().replace(/\s+/g, " ");
    if (seenLines.has(key)) continue;
    seenLines.add(key);
    const section = line.match(/\bSection\s+['"]?([A-Z])['"]?\s*[-–]\s*['"]?([A-Z])/i);
    const detail = replacement ? `Existing beam replacement${section ? ` — Section ${section[1]}-${section[2]}` : ""}` :
      /\b(?:altered|existing)\b.*\bdoorway\b/i.test(line) ? "Altered existing doorway" :
      /\bnew opening\b/i.test(line) ? line.slice(line.search(/\bnew opening\b/i))
        .replace(/[,;].*$/, "").replace(/\b(?:max(?:imum)?|width|height)\b.*$/i, "").trim().slice(0, 90) :
      line.replace(/\b(?:max(?:imum)?|width|height)\b.*$/i, "").replace(/[,.]\s*$/, "").slice(0, 90);
    const values = [...line.matchAll(/(\d+(?:\.\d+)?)\s*(mm|m)\b/gi)]
      .map(m => Number(m[1]) / (m[2].toLowerCase() === "mm" ? 1000 : 1));
    const ids = evidence.filter(e => e.unit === "m" && /\b(opening|doorway)\b/i.test(e.label) &&
      values.some(v => Math.abs(v - e.value) < 1e-8)).map(e => e.id);
    const label = `Opening ${subjects.length + 1} — ${detail || "Opening"}`;
    subjects.push({ id: `description-opening-${index}`, kind: "opening", label, dimensionEvidenceIds: ids });
  }
  if (/\bchimney\b/i.test(positive)) {
    subjects.push({ id: "description-chimney", kind: "chimney", label: "Chimney breast",
      dimensionEvidenceIds: evidence.filter(e => /\bchimney\b/i.test(e.label)).map(e => e.id) });
  }
  return subjects.slice(0, 25);
}

export function createSubjectFields(rawEvidence: unknown[], context: string[], rawSubjects: unknown[],
  reviewable: (f: MeasurementReviewField, profile?: string) => boolean,
): { fields: MeasurementReviewField[]; jobLabel: string } | undefined {
  const evidence = rawEvidence.flatMap(raw => {
    const parsed = dimensionSchema.safeParse(raw);
    return parsed.success ? [parsed.data] : [];
  });
  let subjects = parseMeasurementSubjects(rawSubjects, evidence);
  if (!subjects.length) subjects = inferMeasurementSubjects(context[0] || "", evidence);
  if (!subjects.length) return undefined;
  subjects = recoverSubjectLinks(subjects, evidence, inferMeasurementSubjects(context[0] || "", evidence))
    .map(subject => ({ ...subject, label: subjectRecognitionLabel(subject, context, evidence) }));
  const used = new Set<string>();
  const fields: MeasurementReviewField[] = [];
  for (const subject of subjects) {
    const linked = evidence.filter(e => subject.dimensionEvidenceIds.includes(e.id))
      .sort((a, b) => Number(isLimit(a)) - Number(isLimit(b)));
    for (const axis of axes[subject.kind]) {
      if (axis === "length" && /^(wall|groundworks)$/.test(subject.kind) && linked.some(e => isConstructionRun(e.label)))
        continue; // Named runs replace an ambiguous extra total-length question.
      const match = linked.find(e => !used.has(e.id) && e.unit === "m" &&
        // Location membership alone cannot turn a window into room geometry,
        // or a trench depth into an extension's front-to-back plan dimension.
        (subject.kind !== "extension" || !keyMeasurementGroup(e.label, e.unit) ||
          ["extension", "footprint"].includes(keyMeasurementGroup(e.label, e.unit)!)) &&
        (subject.kind !== "room" || !keyMeasurementGroup(e.label, e.unit) ||
          keyMeasurementGroup(e.label, e.unit, subject.kind) === "rooms") &&
        (subject.kind !== "wall" || !keyMeasurementGroup(e.label, e.unit) ||
          ["external-walls", "internal-walls"].includes(keyMeasurementGroup(e.label, e.unit)!)) &&
        (subject.kind !== "roof" || !/\b(rooflight|lantern|opening)\b/i.test(e.label) ||
          /\b(rooflight|lantern|opening)\b/i.test(subject.label)) &&
        (!isConstructionRun(e.label) && subjectMeasurementAxis(subject.kind, e.label) === axis ||
          // Actual extraction can name the work area without naming the axis.
          // A linked opening-width callout still has a usable width; a retained
          // wall, bearing, height or cross-section must never become that width.
          (subject.kind === "opening" && axis === "width" &&
            /\b(opening|doorway)\b/i.test(e.label) &&
            !/\b(retain(?:ed)?|height|depth|projection|thickness|section)\b/i.test(e.label) &&
            /\b(width|span|max(?:imum)?\s+opening)\b/i.test(e.note))) &&
        // A beam label may identify an opening, but member/padstone dimensions are never opening geometry.
        !/\b(padstones?|bearings?|plates?|bolts?|flange|section)\b/i.test(e.label) &&
        (!/\b(beams?|steel)\b/i.test(e.label) || /\b(opening|doorway)\b/i.test(e.label)) &&
        reviewable({ ...e, label: `${subject.kind} ${axis}`, value: String(e.value), original: e,
          subjectId: subject.id, subjectKind: subject.kind }, subjectProfile[subject.kind]));
      const group = { subjectId: subject.id, subjectLabel: subject.label, subjectKind: subject.kind };
      const scopedLabel = `${subject.label.slice(0, 117 - axis.length)} — ${axis}`;
      if (match) {
        used.add(match.id);
        const limit = isLimit(match);
        fields.push({ id: limit ? `review-${subject.id}-${axis}` : match.id,
          label: limit || !new RegExp(`\\b${axis}\\b`, "i").test(match.label) ? scopedLabel : match.label,
          unit: match.unit, value: String(match.value),
          original: match, drawingLimit: limit || undefined,
          note: `${match.note} Work area: ${subject.label}.${limit ?
            " Prefilled from the stated drawing limit, not a surveyed actual size. Check or adjust the proposed quote size; the original design limit is retained." : ""}`, ...group });
      } else {
        fields.push({ id: `review-${subject.id}-${axis}`, label: scopedLabel,
          unit: "m", value: "", ...group,
          note: `No ${axis} identified for this work area. Leave blank if unknown.${subject.kind === "chimney" ?
            " Dimensions do not imply removal of the whole chimney stack." : ""}` });
      }
    }
    // Several construction runs may share an axis, but are not duplicate footprint
    // lengths. Keep every named included run editable instead of silently dropping it.
    for (const e of linked) {
      if (used.has(e.id) || !(isConstructionRun(e.label) || keyMeasurementGroup(e.label, e.unit, subject.kind)) || !reviewable({
        ...e, value: String(e.value), original: e, subjectId: subject.id, subjectKind: subject.kind,
      }, undefined)) continue;
      used.add(e.id);
      fields.push({ id: e.id, label: e.label, unit: e.unit, value: String(e.value), note: e.note,
        original: e, subjectId: subject.id, subjectLabel: subject.label, subjectKind: subject.kind });
    }
  }
  return { fields, jobLabel: [...new Set(subjects.map(s => subjectProfile[s.kind]))].join(" · ") };
}
